const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const IPV4_RE = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const HOST_PORT_RE = /\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}:\d{2,5}\b/g;
const LOCALHOST_PORT_RE = /\blocalhost:\d{2,5}\b/gi;
const IP_PORT_RE = /\b(?:\d{1,3}\.){3}\d{1,3}:\d{2,5}\b/g;
const URL_RE = /https?:\/\/[^\s'"<>]+/gi;
const HTTP_POOL_RE = /HTTPConnectionPool\([^)]*\)/gi;
const HOST_KWARG_RE = /(host|hostname)\s*=\s*['"][^'"]+['"]/gi;
const PORT_KWARG_RE = /port\s*=\s*['"]?\d{2,5}['"]?/gi;

const CONNECTION_HINTS = [
  'httpconnectionpool',
  'newconnectionerror',
  'connection refused',
  'failed to establish a new connection',
  'max retries exceeded',
  'remotedisconnected',
  'read timed out',
  'connect timeout',
  'name or service not known',
  'connection error',
  'connectionreseterror',
];

const coerceErrorText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return normalizeWhitespace(value);
  if (Array.isArray(value)) {
    return normalizeWhitespace(
      value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item.msg === 'string') return item.msg;
          return '';
        })
        .filter(Boolean)
        .join(' ')
    );
  }
  if (typeof value === 'object') {
    if (typeof value.msg === 'string') return normalizeWhitespace(value.msg);
    if (typeof value.message === 'string') return normalizeWhitespace(value.message);
    if (typeof value.detail === 'string') return normalizeWhitespace(value.detail);
    try {
      return normalizeWhitespace(JSON.stringify(value));
    } catch {
      return normalizeWhitespace(String(value));
    }
  }
  return normalizeWhitespace(String(value));
};

const looksLikeConnectionError = (text) => {
  const lower = normalizeWhitespace(text).toLowerCase();
  return CONNECTION_HINTS.some((hint) => lower.includes(hint));
};

const friendlyConnectionMessage = (text) => {
  const lower = normalizeWhitespace(text).toLowerCase();
  if (lower.includes('marker') || lower.includes('/parse/')) {
    return 'The PDF parser service could not be reached. Verify that the parser is running and reachable from the application.';
  }
  if (lower.includes('graphrag') || lower.includes('neo4j')) {
    return 'Graph ingestion failed while sending this document to the knowledge graph.';
  }
  if (lower.includes('milvus') || lower.includes('pymilvus') || lower.includes('milvusexception')) {
    if (
      lower.includes('fail connecting to server')
      || lower.includes('server unavailable')
      || lower.includes('illegal connection params')
      || lower.includes('connection refused')
    ) {
      return (
        'Milvus is not running or cannot be reached right now. '
        + 'Please start the vector database service and retry this file.'
      );
    }
    return (
      'Could not save vectors to Milvus while indexing this file. '
      + 'Please verify the vector database is running and retry.'
    );
  }
  if (lower.includes('s3') || lower.includes('knowledge source')) {
    return 'Knowledge Source access failed while reading or writing this file.';
  }
  return 'A required backend service could not be reached. Please try again later.';
};

const matchesPattern = (pattern, text) => {
  pattern.lastIndex = 0;
  return pattern.test(text);
};

export const containsSensitiveEndpointInfo = (value) => {
  const text = coerceErrorText(value);
  if (!text) return false;
  return (
    matchesPattern(IPV4_RE, text)
    || matchesPattern(IP_PORT_RE, text)
    || matchesPattern(HOST_PORT_RE, text)
    || matchesPattern(LOCALHOST_PORT_RE, text)
    || matchesPattern(URL_RE, text)
    || matchesPattern(HTTP_POOL_RE, text)
    || looksLikeConnectionError(text)
  );
};

export const sanitizeErrorMessage = (value, { fallback = 'Something went wrong. Please try again.' } = {}) => {
  const text = coerceErrorText(value);
  if (!text) return fallback;

  if (looksLikeConnectionError(text)) {
    return friendlyConnectionMessage(text);
  }

  let cleaned = text
    .replace(URL_RE, '[service]')
    .replace(HTTP_POOL_RE, 'Connection error')
    .replace(HOST_KWARG_RE, "host='[server]'")
    .replace(PORT_KWARG_RE, 'port=[port]')
    .replace(IP_PORT_RE, '[server]')
    .replace(HOST_PORT_RE, '[server]')
    .replace(LOCALHOST_PORT_RE, '[server]')
    .replace(IPV4_RE, '[server]')
    .replace(/\bport\s+\d{2,5}\b/gi, 'port [port]');

  cleaned = normalizeWhitespace(cleaned);

  if (containsSensitiveEndpointInfo(cleaned)) {
    return fallback;
  }
  if (cleaned.toLowerCase().startsWith('connection error')) {
    return friendlyConnectionMessage(cleaned);
  }

  return cleaned || fallback;
};

export const getApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const detail = error?.response?.data?.detail;
  if (detail != null && detail !== '') {
    return sanitizeErrorMessage(detail, { fallback });
  }
  if (error?.message) {
    return sanitizeErrorMessage(error.message, { fallback });
  }
  return fallback;
};
